/**
 * ScheduleQuestionnairePage — public HMAC-tokenized form a contractor fills
 * out to provide per-stage timing for a project (no login, no pricing).
 *
 * Scope-aware: each trade carries a `scope_basis` set by the owner on the
 * "Send timing questionnaire" screen:
 *   - whole_project → one set of day inputs
 *   - per_building  → one set of day inputs per building
 *   - per_unit      → one set of day inputs per unit
 * A "same days apply to all" fast-path lets the contractor fill one set and
 * we propagate it to every building/unit on submit.
 *
 * Route: /schedule-questionnaire/:token (public, no auth)
 */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle2, AlertTriangle, XCircle, Upload, X, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SCOPE_LABEL = {
  whole_project: 'Whole project',
  per_building: 'Per building',
  per_unit: 'Per unit',
};

// Build the list of day-input groups for a trade given its scope basis.
function groupsFor(basis, buildingCount, unitsPerBuilding) {
  const bc = Math.max(1, parseInt(buildingCount, 10) || 1);
  const upb = Math.max(1, parseInt(unitsPerBuilding, 10) || 1);
  if (basis === 'per_building') {
    return Array.from({ length: bc }, (_, b) => ({
      key: `b${b}`, label: `Building ${b + 1}`, building_index: b, unit_index: null,
    }));
  }
  if (basis === 'per_unit') {
    const total = bc * upb;
    return Array.from({ length: total }, (_, u) => ({
      key: `u${u}`, label: `Unit ${u + 1}`, building_index: null, unit_index: u,
    }));
  }
  return [{ key: 'all', label: 'Whole project', building_index: null, unit_index: null }];
}

export default function ScheduleQuestionnairePage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [ctx, setCtx] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // tradeAnswers[trade] = {
  //   sameForAll, scope_basis,
  //   stages: [{stage_name, is_custom}],
  //   days: { [groupKey]: { [stageName]: value } },   // 'all' key holds the shared set
  //   crew: { [stageName]: value },
  // }
  const [tradeAnswers, setTradeAnswers] = useState({});
  const [newStageInputs, setNewStageInputs] = useState({});
  const [stageRequiresCrew, setStageRequiresCrew] = useState(new Set());
  const [contractFile, setContractFile] = useState(null);
  const [submitterEmail, setSubmitterEmail] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/trade-stages`);
        if (cancelled) return;
        setStageRequiresCrew(new Set((r.data?.stage_requires_crew_size || []).map(s => s.toLowerCase())));
      } catch (e) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/schedule-questionnaire/${token}`);
        if (cancelled) return;
        setCtx(r.data);
        const initial = {};
        const inputs = {};
        (r.data.trades || []).forEach(t => {
          initial[t.trade] = {
            sameForAll: true,
            scope_basis: t.scope_basis || 'whole_project',
            stages: (t.stages || []).map(s => ({ stage_name: s, is_custom: false })),
            days: { all: Object.fromEntries((t.stages || []).map(s => [s, ''])) },
            crew: {},
          };
          inputs[t.trade] = '';
        });
        setTradeAnswers(initial);
        setNewStageInputs(inputs);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || 'This link could not be verified.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  const handleFileSelect = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('File too large — max 5MB.'); return; }
    const reader = new FileReader();
    reader.onload = (e) => setContractFile({ name: file.name, base64: e.target.result });
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    if (!ctx) return;
    const responses = [];
    let missing = 0;
    for (const trade of ctx.trades) {
      const ans = tradeAnswers[trade.trade];
      if (!ans) continue;
      const basis = ans.scope_basis || 'whole_project';
      const groups = groupsFor(basis, ctx.building_count, ctx.units_per_building);
      const stageList = ans.stages || [];
      const crewFor = (stageName) => {
        const v = parseInt((ans.crew || {})[stageName], 10);
        return Number.isFinite(v) && v > 0 ? v : null;
      };
      const singleSet = groups.length === 1 || ans.sameForAll;
      const pushFrom = (dayMap, group) => {
        for (const s of stageList) {
          const days = parseInt(dayMap[s.stage_name], 10);
          if (isNaN(days)) { missing += 1; continue; }
          responses.push({
            trade: trade.trade, stage_name: s.stage_name, days,
            building_index: group.building_index, unit_index: group.unit_index,
            is_custom: !!s.is_custom, crew_size: crewFor(s.stage_name),
          });
        }
      };
      if (singleSet) {
        const shared = ans.days.all || {};
        if (groups.length === 1) {
          pushFrom(shared, groups[0]);
        } else {
          // propagate the one shared set to every building/unit
          for (const g of groups) pushFrom(shared, g);
        }
      } else {
        for (const g of groups) pushFrom(ans.days[g.key] || {}, g);
      }
    }
    if (responses.length === 0) {
      toast.error('Please enter days for at least one stage before submitting.');
      return;
    }
    if (missing > 0) {
      toast.warning(`${missing} stage field${missing === 1 ? '' : 's'} left blank — those will be skipped.`);
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/schedule-questionnaire/${token}/submit`, {
        responses,
        contract_file_base64: contractFile?.base64 || null,
        contract_filename: contractFile?.name || null,
        submitted_by_email: submitterEmail || null,
        notes: notes || null,
      });
      setDone(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
      </div>
    );
  }
  if (error && !ctx) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="schedule-q-error">
          <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Link can&apos;t be used</h1>
          <p className="text-sm text-slate-600">{error}</p>
        </div>
      </div>
    );
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-slate-200 p-8 text-center" data-testid="schedule-q-success">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-slate-900 mb-1">Got it — thanks!</h1>
          <p className="text-sm text-slate-600 mb-1">
            {ctx.sender_name || 'The team'} at {ctx.sender_company || 'DrawReady'} now has your timing.
          </p>
          <p className="text-xs text-slate-400">You can close this tab.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="schedule-questionnaire-page">
      <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="px-7 pt-7 pb-4 border-b border-slate-100">
          <div className="text-[10px] font-bold uppercase tracking-wider text-blue-700 mb-1">
            DrawReady · Scheduling input
          </div>
          <h1 className="text-xl font-bold text-slate-900">
            How many days does each stage take?
          </h1>
          <p className="text-sm text-slate-600 mt-1.5">
            {ctx.sender_name}{ctx.sender_company ? ` (${ctx.sender_company})` : ''} is scheduling{' '}
            <strong>{ctx.project_name || 'a project'}</strong>
            {ctx.project_address && <span className="text-slate-400"> · {ctx.project_address}</span>}.
          </p>
          {ctx.note_for_contractor && (
            <div className="mt-3 bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-700 italic">
              &ldquo;{ctx.note_for_contractor}&rdquo;
            </div>
          )}
        </div>

        {ctx.already_responded && (
          <div className="mx-7 mt-4 bg-amber-50 border border-amber-200 rounded-lg p-3 flex gap-2 items-start">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="text-xs text-amber-800">
              You already submitted this on {ctx.submitted_at?.slice(0, 10)}. Submitting again will replace your previous answers.
            </div>
          </div>
        )}

        <div className="px-7 py-5 space-y-5">
          {(ctx.trades || []).map((trade) => {
            const ans = tradeAnswers[trade.trade] || { sameForAll: true, scope_basis: 'whole_project', stages: [], days: { all: {} }, crew: {} };
            const basis = ans.scope_basis || 'whole_project';
            const stageList = ans.stages || [];
            const groups = groupsFor(basis, ctx.building_count, ctx.units_per_building);
            const isMultiGroup = groups.length > 1;
            const usingShared = !isMultiGroup || ans.sameForAll;

            const setSame = (val) => setTradeAnswers(prev => ({ ...prev, [trade.trade]: { ...prev[trade.trade], sameForAll: val } }));
            const setStageDays = (groupKey, stage, val) => setTradeAnswers(prev => ({
              ...prev,
              [trade.trade]: {
                ...prev[trade.trade],
                days: { ...prev[trade.trade].days, [groupKey]: { ...(prev[trade.trade].days[groupKey] || {}), [stage]: val } },
              },
            }));
            const setStageCrew = (stage, val) => setTradeAnswers(prev => ({
              ...prev,
              [trade.trade]: { ...prev[trade.trade], crew: { ...(prev[trade.trade].crew || {}), [stage]: val } },
            }));
            const removeCustomStage = (stageName) => setTradeAnswers(prev => {
              const next = { ...prev[trade.trade] };
              next.stages = next.stages.filter(s => s.stage_name !== stageName);
              const newDays = {};
              for (const [gk, m] of Object.entries(next.days || {})) {
                const { [stageName]: _o, ...rest } = m;
                newDays[gk] = rest;
              }
              next.days = newDays;
              return { ...prev, [trade.trade]: next };
            });
            const addCustomStage = () => {
              const name = (newStageInputs[trade.trade] || '').trim();
              if (!name) return;
              if (stageList.some(s => s.stage_name.toLowerCase() === name.toLowerCase())) {
                toast.error(`"${name}" is already in your stage list.`);
                return;
              }
              setTradeAnswers(prev => ({
                ...prev,
                [trade.trade]: {
                  ...prev[trade.trade],
                  stages: [...(prev[trade.trade].stages || []), { stage_name: name, is_custom: true }],
                },
              }));
              setNewStageInputs(prev => ({ ...prev, [trade.trade]: '' }));
            };

            // total: shared set counts once; per-group sums each group
            const sumSet = (m) => stageList.reduce((s, st) => s + (parseInt((m || {})[st.stage_name], 10) || 0), 0);
            const total = usingShared
              ? sumSet(ans.days.all)
              : groups.reduce((s, g) => s + sumSet(ans.days[g.key]), 0);

            const renderStageRows = (groupKey) => (
              <div className="space-y-2">
                {stageList.map((s) => {
                  const dk = `${trade.trade.toLowerCase()}.${s.stage_name.toLowerCase()}`;
                  const needsCrew = stageRequiresCrew.has(dk);
                  return (
                    <div key={s.stage_name} className="flex items-center gap-3 flex-wrap">
                      <span className="text-xs font-medium text-slate-700 w-44 flex-shrink-0 flex items-center gap-1">
                        {s.stage_name}
                        {s.is_custom && (
                          <span className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 border border-amber-200 px-1 rounded">custom</span>
                        )}
                      </span>
                      <Input
                        type="number" min="0" step="1"
                        value={(ans.days[groupKey] || {})[s.stage_name] ?? ''}
                        onChange={(e) => setStageDays(groupKey, s.stage_name, e.target.value)}
                        placeholder="e.g. 3"
                        className="w-24 text-center"
                        data-testid={`stage-input-${trade.trade}-${groupKey}-${s.stage_name.replace(/\s+/g, '-')}`}
                      />
                      <span className="text-xs text-slate-500">days</span>
                      {needsCrew && groupKey === 'all' && (
                        <>
                          <span className="text-xs text-slate-400 mx-1">·</span>
                          <Input
                            type="number" min="1" step="1"
                            value={(ans.crew || {})[s.stage_name] ?? ''}
                            onChange={(e) => setStageCrew(s.stage_name, e.target.value)}
                            placeholder="2"
                            className="w-16 text-center"
                            data-testid={`stage-crew-${trade.trade}-${s.stage_name.replace(/\s+/g, '-')}`}
                          />
                          <span className="text-xs text-slate-500 whitespace-nowrap">guys / unit</span>
                        </>
                      )}
                      {s.is_custom && (
                        <button
                          type="button"
                          onClick={() => removeCustomStage(s.stage_name)}
                          className="text-slate-400 hover:text-red-600 ml-1"
                          data-testid={`remove-stage-${trade.trade}-${s.stage_name.replace(/\s+/g, '-')}`}
                          title="Remove this custom stage"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );

            return (
              <div key={trade.trade} className="border border-slate-200 rounded-xl p-4" data-testid={`trade-block-${trade.trade}`}>
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-base font-bold text-slate-900 capitalize">{trade.trade}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                      {stageList.length} stage{stageList.length === 1 ? '' : 's'}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full border border-slate-200" data-testid={`scope-badge-${trade.trade}`}>
                      {SCOPE_LABEL[basis] || 'Whole project'}
                    </span>
                  </div>
                  {isMultiGroup && (
                    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ans.sameForAll}
                        onChange={(e) => setSame(e.target.checked)}
                        className="rounded"
                        data-testid={`same-for-all-${trade.trade}`}
                      />
                      Same days apply to all {groups.length} {basis === 'per_unit' ? 'units' : 'buildings'}
                    </label>
                  )}
                </div>

                {usingShared ? (
                  renderStageRows('all')
                ) : (
                  <div className="space-y-3">
                    {stageList.some(s => stageRequiresCrew.has(`${trade.trade.toLowerCase()}.${s.stage_name.toLowerCase()}`)) && (
                      <div className="bg-white border border-slate-300 rounded-lg p-3" data-testid={`crew-block-${trade.trade}`}>
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                          Crew size (applies to all {basis === 'per_unit' ? 'units' : 'buildings'})
                        </div>
                        <div className="space-y-1.5">
                          {stageList.map((s) => {
                            const dk = `${trade.trade.toLowerCase()}.${s.stage_name.toLowerCase()}`;
                            if (!stageRequiresCrew.has(dk)) return null;
                            return (
                              <div key={`crew-${s.stage_name}`} className="flex items-center gap-2">
                                <span className="text-xs font-medium text-slate-700 w-40 flex-shrink-0">{s.stage_name}</span>
                                <Input
                                  type="number" min="1" step="1"
                                  value={(ans.crew || {})[s.stage_name] ?? ''}
                                  onChange={(e) => setStageCrew(s.stage_name, e.target.value)}
                                  placeholder="2"
                                  className="w-16 text-center"
                                  data-testid={`stage-crew-pb-${trade.trade}-${s.stage_name.replace(/\s+/g, '-')}`}
                                />
                                <span className="text-xs text-slate-500">guys / unit</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                    {groups.map((g) => (
                      <div key={g.key} className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
                          {g.label}
                        </div>
                        {renderStageRows(g.key)}
                      </div>
                    ))}
                  </div>
                )}

                {/* + Add custom stage */}
                <div className="mt-3 flex items-center gap-2">
                  <Input
                    value={newStageInputs[trade.trade] || ''}
                    onChange={(e) => setNewStageInputs(prev => ({ ...prev, [trade.trade]: e.target.value }))}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomStage(); } }}
                    placeholder={`Add another stage for ${trade.trade} (e.g. Sealing)`}
                    className="flex-1 text-xs"
                    data-testid={`new-stage-input-${trade.trade}`}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={addCustomStage}
                    disabled={!(newStageInputs[trade.trade] || '').trim()}
                    data-testid={`add-stage-btn-${trade.trade}`}
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add stage
                  </Button>
                </div>

                <div className="mt-3 pt-2 border-t border-slate-100 flex justify-between items-center text-xs">
                  <span className="text-slate-500">Total for this trade</span>
                  <span className="font-bold text-slate-900" data-testid={`trade-total-${trade.trade}`}>{total} days</span>
                </div>
              </div>
            );
          })}

          {/* Optional contract upload */}
          <div className="border border-dashed border-slate-300 rounded-xl p-4" data-testid="contract-upload-block">
            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
              Optional — upload your contract / proposal
            </Label>
            <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
              If you already have a signed contract or proposal with the owner, drop it here for their records (PDF / image, 5MB max).
            </p>
            {contractFile ? (
              <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg p-2 text-xs">
                <span className="text-slate-700 truncate">{contractFile.name}</span>
                <button onClick={() => setContractFile(null)} className="text-slate-400 hover:text-red-600" data-testid="clear-contract-btn">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-1.5 text-xs text-blue-700 hover:text-blue-800 cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                Choose file
                <input
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                  className="hidden"
                  data-testid="contract-file-input"
                  onChange={(e) => handleFileSelect(e.target.files?.[0])}
                />
              </label>
            )}
          </div>

          {/* Optional sender info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Your email (optional)</Label>
              <Input
                type="email" value={submitterEmail}
                onChange={(e) => setSubmitterEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                data-testid="submitter-email-input"
              />
            </div>
            <div>
              <Label className="text-[11px] font-bold uppercase tracking-wider text-slate-600">Notes (optional)</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Weather dependent · Crew of 4"
                data-testid="submitter-notes-input"
              />
            </div>
          </div>

          <Button
            size="lg"
            disabled={submitting}
            onClick={handleSubmit}
            className="w-full bg-blue-700 hover:bg-blue-800 text-white font-semibold"
            data-testid="submit-questionnaire-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Submit timing
          </Button>

          <p className="text-[10px] text-slate-400 text-center">
            This link is signed and unique to you · expires in 30 days.
          </p>
        </div>
      </div>
    </div>
  );
}
